import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconPlus, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listReceipts, type ReceiptDto } from './api';
import { NewReceiptModal } from './NewReceiptModal';
import { ReceiptStatusBadge } from './ReceiptStatusBadge';

/** Open means "still being keyed in", which is the only thing anybody acts on. */
type Scope = 'open' | 'all';

/**
 * Receiving — the list of receipt batches.
 *
 * Opens on the ones still being keyed rather than everything ever booked in, because a posted
 * receipt from March is a document, not work. Switching to All is how somebody goes back to it.
 */
export function ReceiptsRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false);

  const [scope, setScope] = useState<Scope>('open');
  // The warehouse comes from the header, not from a filter on this screen. There used to be an
  // "All warehouses" option here; it cannot mean that any more — permissions resolve per warehouse,
  // so a list pooled across them would show rows the session has no rights to, and naming a
  // different warehouse than the header earns a 403.
  const warehouseId = useCurrentWarehouseId();
  const [search, setSearch] = useState('');

  const canPost = permissions.has('receipts:post');

  const receipts = useQuery({
    queryKey: ['receipts', scope, warehouseId] as const,
    queryFn: ({ signal }) =>
      listReceipts(
        {
          openOnly: scope === 'open',
          warehouseId,
        },
        signal,
      ),
  });

  // Client-side, over what the server already narrowed. Somebody looking for a delivery knows the
  // packing slip in their hand or the PO number on it, and a round trip per keystroke to re-ask a
  // question already answered is waste.
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return receipts.data ?? [];
    return (receipts.data ?? []).filter(
      (r) =>
        r.batchNumber.toLowerCase().includes(needle) ||
        (r.poNumber ?? '').toLowerCase().includes(needle) ||
        (r.supplierName ?? '').toLowerCase().includes(needle) ||
        (r.packingSlipNumber ?? '').toLowerCase().includes(needle),
    );
  }, [receipts.data, search]);

  function when(iso: string | null): string {
    return iso ? new Date(iso).toLocaleDateString() : '—';
  }

  /**
   * The batch number opens the receipt, so it is the headline and therefore the tap target. The
   * supplier is the subtitle because that is how somebody holding a delivery note recognises the
   * row; the PO number drops to a labelled row, since it is what they check afterwards.
   */
  const columns: DataColumn<ReceiptDto>[] = useMemo(
    () => [
      {
        key: 'batchNumber',
        label: t('receipts.batchNumber'),
        card: 'title',
        render: (r) => (
          <>
            <Anchor
              fw={500}
              onClick={() =>
                void navigate({ to: '/app/receiving/$id', params: { id: r.receiptBatchId } })
              }
            >
              {r.batchNumber}
            </Anchor>
            {r.packingSlipNumber && (
              <Text size="xs" c="dimmed">
                {t('receipts.packingSlipShort', { number: r.packingSlipNumber })}
              </Text>
            )}
          </>
        ),
      },
      {
        key: 'supplier',
        label: t('receipts.supplier'),
        card: 'subtitle',
        render: (r) => (
          <>
            <Text size="sm">{r.supplierName ?? '—'}</Text>
            {r.supplierNumber && (
              <Text size="xs" c="dimmed">
                {r.supplierNumber}
              </Text>
            )}
          </>
        ),
      },
      {
        key: 'status',
        label: t('receipts.status'),
        card: 'aside',
        render: (r) => <ReceiptStatusBadge status={r.status} />,
      },
      {
        key: 'poNumber',
        label: t('receipts.poNumber'),
        render: (r) => r.poNumber ?? '—',
      },
      {
        key: 'warehouse',
        label: t('receipts.warehouse'),
        render: (r) => r.warehouseCode,
      },
      {
        key: 'started',
        label: t('receipts.started'),
        render: (r) => when(r.createdAtUtc),
      },
      {
        key: 'lines',
        label: t('receipts.lines'),
        align: 'right',
        // A batch with no lines cannot be posted (53831), so it is worth calling out rather than
        // showing a quiet 0.
        render: (r) =>
          r.lineCount === 0 ? (
            <Badge color="yellow" variant="light">
              {t('receipts.noLines')}
            </Badge>
          ) : (
            r.lineCount
          ),
      },
      {
        key: 'totalQuantity',
        label: t('receipts.totalQuantity'),
        align: 'right',
        render: (r) => r.totalQuantity.toLocaleString(),
      },
    ],
    [t, navigate],
  );

  return (
    <>
      <PageHeader
        title={t('nav.receiving')}
        actions={
          canPost && (
            <Button
              color="invetrak"
              leftSection={<IconPlus size={16} />}
              onClick={openCreate}
            >
              {t('receipts.new')}
            </Button>
          )
        }
      />

      {/* No "you have no warehouses" branch here any more: WarehouseGate does not render a
          screen until the session is in one, so this used to be unreachable code describing a
          state the shell now handles for every screen at once. */}
      <Stack gap="md">
          <Group gap="sm" align="flex-end" wrap="wrap">
            <SegmentedControl
              value={scope}
              onChange={(v) => setScope(v as Scope)}
              data={[
                { value: 'open', label: t('receipts.scopeOpen') },
                { value: 'all', label: t('receipts.scopeAll') },
              ]}
            />
            <TextInput
              label={t('receipts.search')}
              placeholder={t('receipts.searchPlaceholder')}
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={{ base: '100%', sm: 280 }}
            />
          </Group>

          {receipts.isLoading ? (
            <Center py="xl">
              <Loader color="invetrak" />
            </Center>
          ) : receipts.isError ? (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('receipts.errors.listFailed')}
            </Alert>
          ) : visible.length === 0 ? (
            <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
              {scope === 'open' ? t('receipts.noneOpen') : t('receipts.none')}
            </Alert>
          ) : (
            <ResponsiveTable
              rows={visible}
              columns={columns}
              rowKey={(r) => r.receiptBatchId}
              minWidth={900}
              striped
              highlightOnHover
            />
          )}
      </Stack>

      <NewReceiptModal
        opened={createOpen}
        onClose={closeCreate}
        onCreated={(r) =>
          void navigate({ to: '/app/receiving/$id', params: { id: r.receiptBatchId } })
        }
      />
    </>
  );
}
